import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import QRCode from 'qrcode';
import { LAYOUTS } from '../../utils/theme';
import { Avatar } from '../CardDisplay';
import Pricing from './Pricing';
import { TEAM_MIN_SEATS, TRIAL_DAYS } from '../../config/plans';
import { usePricing } from '../../lib/pricing';
import {
  Check, MiniPhone, SAMPLE_CARD, SAMPLE_THEME, SoonTag, TEAM_CARDS, TEAM_THEME, signupPath, usePageTitle,
} from './shared';
import { BRAND } from '../../config/brand';

/** Phone width aware scale for the hero phone. */
function useHeroScale() {
  const get = () => (typeof window === 'undefined' ? 0.9 : window.innerWidth < 400 ? 0.8 : window.innerWidth < 1024 ? 0.88 : 0.94);
  const [scale, setScale] = useState(get);
  useEffect(() => {
    const on = () => setScale(get());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return scale;
}

const container = 'max-w-6xl mx-auto px-4 sm:px-6';
const ctaPrimary = 'inline-flex items-center justify-center h-12 px-6 rounded-xl bg-blue text-white font-semibold text-[16px] hover:bg-blue-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition';
const ctaGhostDark = 'inline-flex items-center justify-center h-12 px-6 rounded-xl border border-white/35 text-white font-semibold text-[16px] hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition';

export default function SalesPage() {
  usePageTitle(`${BRAND.name} · Digital business cards that bring leads back`);
  return (
    <div className="bg-white text-ink font-sans overflow-x-hidden">
      <style>{`
        @keyframes tz-ring { 0% { opacity: 0; transform: scale(.6) } 35% { opacity: 1 } 100% { opacity: 0; transform: scale(1.25) } }
        .tz-ring { transform-origin: 50% 50%; animation: tz-ring 2.4s cubic-bezier(.2,.7,.2,1) 1 both; }
        .tz-ring-2 { animation-delay: .25s } .tz-ring-3 { animation-delay: .5s }
        @media (prefers-reduced-motion: reduce) { .tz-ring { animation: none; opacity: 0 } }
      `}</style>
      <TopBar />
      <Hero />
      <HowItWorks />
      <Layouts />
      <LeadCapture />
      <ForTeams />
      <Insights />
      <ComingSoon />
      <Pricing />
      <Faq />
      <FinalCta />
      <Footer />
    </div>
  );
}

function TopBar() {
  return (
    <header className="bg-navy text-white">
      <div className={`${container} h-16 flex items-center gap-4`}>
        <img src={BRAND.logo.lockupOnDark} alt={BRAND.name} className="h-8 w-auto" />
        <nav className="hidden md:flex items-center gap-1 ml-6 text-[15px] text-[#C9D6E6]" aria-label="Sections">
          <a href="#how" className="px-3 py-2 rounded-lg hover:text-white">How it works</a>
          <a href="#features" className="px-3 py-2 rounded-lg hover:text-white">Features</a>
          <a href="#pricing" className="px-3 py-2 rounded-lg hover:text-white">Pricing</a>
          <a href="#faq" className="px-3 py-2 rounded-lg hover:text-white">Questions</a>
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/login" className="h-10 px-3 rounded-lg text-[15px] font-medium text-[#C9D6E6] hover:text-white flex items-center">Sign in</Link>
          <Link to={signupPath('free')} className="h-10 px-4 rounded-lg bg-white text-navy text-[15px] font-semibold flex items-center hover:bg-blue-soft">Start free</Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  const [layout, setLayout] = useState('classic');
  const scale = useHeroScale();
  return (
    <section className="bg-navy text-white relative">
      <div className={`${container} grid lg:grid-cols-[1.3fr_1fr] gap-12 lg:gap-8 items-center pt-10 pb-16 lg:pt-16 lg:pb-24`}>
        <div className="max-w-[720px]">
          <h1 className="font-display font-extrabold text-[40px] leading-[42px] min-[400px]:text-[44px] min-[400px]:leading-[46px] sm:text-[64px] sm:leading-[64px] lg:text-[60px] lg:leading-[60px] xl:text-[68px] xl:leading-[68px] tracking-[-0.035em]">
            <span className="block">Share your card.</span>
            <span className="block">Leave with theirs.</span>
          </h1>
          <p className="mt-6 text-[18px] leading-[28px] sm:text-[20px] sm:leading-[30px] text-[#C9D6E6] max-w-[34em]">
            Carduzo is a digital business card that opens from a QR code, saves to their phone in one tap,
            and asks for their name and email in return. You get the contact you would have lost.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={signupPath('free')} className={ctaPrimary}>Start free</Link>
            <a href="#pricing" className={ctaGhostDark}>See pricing</a>
          </div>
          <p className="mt-4 text-[14px] text-[#9FB3CC]">Free forever for one card. No app for you or the people you meet.</p>
        </div>

        <div className="flex flex-col items-center gap-6">
          <div className="relative">
            <svg viewBox="0 0 300 300" className="absolute left-[calc(100%-40px)] top-[18%] w-[260px] h-[260px] pointer-events-none hidden sm:block" aria-hidden="true">
              <g fill="none" stroke="#8B5CF6" strokeWidth="9" strokeLinecap="round">
                <path d="M20 190a70 70 0 0 1 70-70" opacity="0.95" />
                <path d="M20 190a120 120 0 0 1 120-120" opacity="0.6" />
                <path d="M20 190a170 170 0 0 1 170-170" opacity="0.3" />
              </g>
              <g fill="none" stroke="#8B5CF6" strokeWidth="2.5">
                <circle className="tz-ring" cx="20" cy="190" r="70" />
                <circle className="tz-ring tz-ring-2" cx="20" cy="190" r="120" />
                <circle className="tz-ring tz-ring-3" cx="20" cy="190" r="170" />
              </g>
            </svg>
            <MiniPhone card={SAMPLE_CARD} theme={SAMPLE_THEME} layout={layout} scale={scale} height={740} className="relative rounded-[36px] p-2.5 !bg-[#1B2C40] ring-1 ring-white/10" />
          </div>
          <div className="w-full max-w-[420px]">
            <p id="layout-label" className="text-center text-[14px] text-[#9FB3CC] mb-2.5">Try the five layouts</p>
            <div role="radiogroup" aria-labelledby="layout-label" className="flex flex-wrap justify-center gap-1.5">
              {LAYOUTS.map((l) => {
                const on = l.id === layout;
                return (
                  <button key={l.id} type="button" role="radio" aria-checked={on} onClick={() => setLayout(l.id)}
                    className={`h-9 px-3.5 rounded-full text-[14px] font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${on ? 'bg-white text-navy' : 'text-white border border-white/25 hover:border-white/60'}`}>
                    {l.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionHead({ id, title, intro, dark = false }) {
  return (
    <div className="max-w-[720px]">
      <h2 id={id} className={`font-display font-extrabold text-[34px] leading-[38px] sm:text-[46px] sm:leading-[50px] tracking-[-0.03em] [text-wrap:balance] ${dark ? 'text-white' : 'text-ink'}`}>{title}</h2>
      {intro && <p className={`mt-4 text-[18px] leading-[28px] ${dark ? 'text-[#C9D6E6]' : 'text-ink-muted'}`}>{intro}</p>}
    </div>
  );
}

function HowItWorks() {
  const [qr, setQr] = useState('');
  useEffect(() => {
    QRCode.toDataURL(BRAND.appUrl, { width: 320, margin: 0, color: { dark: '#0B1530', light: '#FFFFFF' } })
      .then(setQr).catch(() => {});
  }, []);
  const steps = [
    {
      title: 'They scan your QR code',
      body: 'Show it on your phone, print it on a badge or a table sign. Any phone camera opens it.',
      visual: (
        <div className="w-[132px] h-[132px] rounded-2xl bg-white border border-line p-3 shadow-sm">
          {qr ? <img src={qr} alt="" className="w-full h-full" /> : <div className="w-full h-full bg-surface rounded" />}
        </div>
      ),
    },
    {
      title: 'Your card opens',
      body: 'No app to install. Your photo, title and every way to reach you, in your colors.',
      visual: <MiniPhone card={SAMPLE_CARD} theme={SAMPLE_THEME} layout="banner" scale={0.3} height={680} className="rounded-[20px] p-1.5" />,
    },
    {
      title: 'They save you, and share back',
      body: 'Save contact adds you to their phone. With lead capture on, they’re asked for their name and email or phone first. A small link always lets them skip.',
      visual: <LeadSheetMock compact />,
    },
    {
      title: 'You follow up',
      body: 'Their details land on your Leads page and in your inbox, ready to call, email or export.',
      visual: <LeadsListMock compact />,
    },
  ];
  return (
    <section id="how" aria-labelledby="how-title" className="py-20 sm:py-28 bg-surface">
      <div className={container}>
        <SectionHead id="how-title" title="From hello to their details in one scan" />
        <ol className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
          {steps.map((s, i) => (
            <li key={s.title} className="flex flex-col">
              <div className="min-h-[150px] sm:h-[220px] flex items-end">{s.visual}</div>
              <div className="mt-6 flex items-baseline gap-3 border-t-2 border-navy pt-4">
                <span className="font-display font-extrabold text-[22px] text-blue leading-none">{i + 1}</span>
                <h3 className="font-display font-bold text-[21px] leading-[26px]">{s.title}</h3>
              </div>
              <p className="mt-2 text-[16px] leading-[25px] text-ink-muted">{s.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/** A still picture of the lead form visitors see (the real one is LeadForm.jsx). */
function LeadSheetMock({ compact = false }) {
  const first = SAMPLE_CARD.full_name.split(' ')[0];
  if (compact) {
    return (
      <div className="w-[200px] rounded-2xl bg-white border border-line shadow-sm p-3.5 text-left" aria-hidden="true">
        <p className="font-display font-bold text-[14px] leading-[18px]">Share your details with {first}</p>
        <div className="mt-2.5 space-y-1.5">
          <div className="h-7 rounded-lg border border-line-strong px-2 text-[11px] text-ink-muted flex items-center">Your name</div>
          <div className="h-7 rounded-lg border border-line-strong px-2 text-[11px] text-ink-muted flex items-center">Email or phone</div>
          <div className="h-7 rounded-lg bg-blue text-white text-[11px] font-semibold flex items-center justify-center">Share &amp; save contact</div>
        </div>
        <p className="mt-2 text-center text-[10px] text-ink-muted underline">Download the contact without sharing</p>
      </div>
    );
  }
  return (
    <div className="w-full max-w-[320px] sm:w-[290px] shrink-0 rounded-[28px] bg-white shadow-[0_24px_60px_-20px_rgba(11,21,48,0.35)] border border-line p-6 text-left" aria-hidden="true">
      <Avatar card={SAMPLE_CARD} t={{ accentSoft: '#E8EEFF', accent_color: '#265EE6' }} size={52} ringWidth={0} />
      <p className="mt-3 font-display font-extrabold text-[22px] leading-7 tracking-tight">Share your details with {first}</p>
      <p className="mt-0.5 text-[14px] text-ink-muted">Send your contact and get {first}’s in return.</p>
      <div className="mt-5 space-y-2.5">
        {['Your name *', 'Email', 'Phone', 'Company (optional)'].map((f) => (
          <div key={f} className="h-11 rounded-xl border border-line-strong px-3.5 text-[15px] text-ink-muted flex items-center">{f}</div>
        ))}
        <div className="h-12 rounded-xl bg-blue text-white font-semibold text-[15px] flex items-center justify-center">Share &amp; save {first}’s contact</div>
      </div>
      <p className="mt-4 text-center text-[13px] text-ink-muted underline">Download the contact without sharing</p>
    </div>
  );
}

const EXAMPLE_LEADS = [
  { name: 'Alex Moreno', detail: 'alex@example.com', company: 'Coastal Builders', when: 'Today' },
  { name: 'Jordan Lee', detail: '(305) 555-0119', company: 'Lee & Partners', when: 'Yesterday' },
  { name: 'Sam Ortiz', detail: 'sam@example.com', company: '', when: 'Tue' },
];

function EmailAlertMock() {
  return (
    <div className="rounded-2xl bg-white border border-line shadow-sm p-4 flex gap-3 text-left" aria-hidden="true">
      <span className="w-9 h-9 rounded-full bg-navy flex items-center justify-center shrink-0">
        <img src={BRAND.logo.mark} alt="" className="h-5 w-auto" />
      </span>
      <span className="min-w-0">
        <span className="block text-[14px] font-semibold">New lead: Alex Moreno</span>
        <span className="block text-[13px] text-ink-muted">Alex shared their details from your card.</span>
      </span>
    </div>
  );
}

function LeadsListMock({ compact = false }) {
  const rows = compact ? EXAMPLE_LEADS.slice(0, 2) : EXAMPLE_LEADS;
  return (
    <div className={`${compact ? 'w-[220px] p-3' : 'w-full p-5'} rounded-2xl bg-white border border-line shadow-sm text-left`} aria-hidden="true">
      <div className="flex items-center justify-between">
        <p className={`font-display font-bold ${compact ? 'text-[14px]' : 'text-[20px]'}`}>Leads</p>
        {!compact && <span className="h-8 px-3 rounded-lg border border-line-strong text-[13px] font-semibold flex items-center">Download CSV</span>}
      </div>
      <ul className={`${compact ? 'mt-2' : 'mt-4'} divide-y divide-line`}>
        {rows.map((r) => (
          <li key={r.name} className={`flex items-center gap-2.5 ${compact ? 'py-1.5' : 'py-3'}`}>
            <span className={`${compact ? 'w-6 h-6 text-[10px]' : 'w-9 h-9 text-[13px]'} rounded-full bg-blue-soft text-blue font-display font-bold flex items-center justify-center shrink-0`}>
              {r.name.split(' ').map((w) => w[0]).join('')}
            </span>
            <span className="min-w-0 flex-1">
              <span className={`block font-semibold truncate ${compact ? 'text-[11px]' : 'text-[15px]'}`}>{r.name}</span>
              <span className={`block text-ink-muted truncate ${compact ? 'text-[10px]' : 'text-[13px]'}`}>{[r.detail, r.company].filter(Boolean).join(', ')}</span>
            </span>
            {!compact && <span className="text-[13px] text-ink-muted">{r.when}</span>}
          </li>
        ))}
      </ul>
      {!compact && <p className="mt-3 text-[12px] text-ink-muted">Example data</p>}
    </div>
  );
}

function Layouts() {
  return (
    <section id="features" aria-labelledby="layouts-title" className="py-20 sm:py-28">
      <div className={container}>
        <SectionHead id="layouts-title" title="Five layouts. Your photo on every one."
          intro="Pick the look that fits you and switch any time. Your link and QR code stay the same, so cards you already shared update too." />
      </div>
      <div className="mt-12 overflow-x-auto pb-4 [scrollbar-width:thin]" tabIndex={0} aria-label="The five card layouts">
        <ul className={`${container} flex gap-5 lg:justify-between w-max lg:w-auto`}>
          {LAYOUTS.map((l) => (
            <li key={l.id} className="flex flex-col items-start gap-3">
              <MiniPhone card={SAMPLE_CARD} theme={SAMPLE_THEME} layout={l.id} scale={0.52} height={700} />
              <div>
                <p className="font-display font-bold text-[18px]">{l.name}</p>
                <p className="text-[14px] text-ink-muted max-w-[200px]">{l.description}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function LeadCapture() {
  const points = [
    'Turn it on or off per card',
    'Visitors can always skip with one small link',
    'Every lead is emailed to you as it arrives',
    'Search your leads and export them to CSV',
  ];
  return (
    <section aria-labelledby="leads-title" className="py-20 sm:py-28 bg-blue-soft">
      <div className={`${container} grid lg:grid-cols-[1fr_auto] gap-14 items-center`}>
        <div>
          <SectionHead id="leads-title" title="Most cards only go one way. Yours asks back."
            intro="When someone taps Save contact, Carduzo can ask for their name and email or phone first. You get a list of everyone who wanted to stay in touch, instead of a pocket of paper cards." />
          <ul className="mt-8 space-y-3">
            {points.map((p) => (
              <li key={p} className="flex gap-3 text-[17px] leading-[26px]"><Check />{p}</li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col sm:flex-row items-center sm:items-start justify-center gap-5">
          <LeadSheetMock />
          <div className="w-full max-w-[320px] sm:w-[290px] shrink-0 flex flex-col gap-4 sm:pt-16">
            <EmailAlertMock />
            <LeadsListMock />
          </div>
        </div>
      </div>
    </section>
  );
}

function ForTeams() {
  return (
    <section aria-labelledby="teams-title" className="py-20 sm:py-28 bg-navy text-white">
      <div className={`${container} grid lg:grid-cols-[1fr_1.1fr] gap-14 items-center`}>
        <div>
          <SectionHead dark id="teams-title" title="One brand for the whole team"
            intro="Set your logo, colors and default layout once. Every card you issue follows it, and you can lock the layout so nobody drifts off brand." />
          <ul className="mt-8 space-y-3 text-[17px] leading-[26px] text-[#E8EEFF]">
            <li className="flex gap-3"><Check className="text-blue-light" />Company branding on every card</li>
            <li className="flex gap-3"><Check className="text-blue-light" />Lock the layout across the team</li>
            <li className="flex gap-3"><Check className="text-blue-light" />Turn lead capture on for everyone at once</li>
            <li className="flex gap-3"><Check className="text-blue-light" />See every lead your team collects in one list</li>
          </ul>
        </div>
        <div className="flex justify-center gap-3 sm:gap-5">
          {TEAM_CARDS.map((c, i) => (
            <MiniPhone key={c.id} card={c} theme={TEAM_THEME} scale={0.42} height={680}
              className={`!bg-[#1B2C40] ring-1 ring-white/10 ${i === 1 ? 'sm:-translate-y-6' : ''} ${i === 2 ? 'hidden sm:block' : ''}`} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Insights() {
  const rows = [['Views', 'People who opened your card'], ['Contacts saved', 'People who added you to their phone'], ['Leads', 'People who shared their details']];
  return (
    <section aria-labelledby="insights-title" className="py-20 sm:py-28">
      <div className={`${container} grid lg:grid-cols-2 gap-12 items-center`}>
        <SectionHead id="insights-title" title="Know if your card is working"
          intro="Your dashboard shows the last 30 days at a glance: how many people opened your card, saved your contact and shared their details." />
        <dl className="grid gap-0 border-t-2 border-navy">
          {rows.map(([k, v]) => (
            <div key={k} className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-x-6 gap-y-1 py-5 border-b border-line">
              <dt className="font-display font-bold text-[26px] leading-8">{k}</dt>
              <dd className="text-[16px] text-ink-muted">{v}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

function ComingSoon() {
  return (
    <section aria-labelledby="soon-title" className="pb-20 sm:pb-28">
      <div className={`${container} grid md:grid-cols-2 gap-5`}>
        <h2 id="soon-title" className="sr-only">Coming soon</h2>
        <div className="rounded-3xl bg-surface p-7 sm:p-9 flex flex-col sm:flex-row gap-7 items-start sm:items-center overflow-hidden">
          <div className="w-[180px] aspect-[1.586] rounded-xl bg-blue p-3.5 relative -rotate-6 shadow-[0_14px_30px_rgba(11,21,48,0.3)] shrink-0" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 22 22" className="absolute right-3 top-3"><circle cx="7" cy="15" r="4" fill="#FF5E5B" /><path d="M7 5a10 10 0 0 1 10 10" fill="none" stroke="#FF5E5B" strokeWidth="2.4" strokeLinecap="round" /></svg>
            <img src={BRAND.logo.wordmarkWhite} alt="" className="h-4 w-auto absolute left-3.5 bottom-3" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2.5"><h3 className="font-display font-bold text-[24px]">NFC cards</h3><SoonTag /></div>
            <p className="mt-2 text-[16px] leading-[25px] text-ink-muted">A printed plastic card in your design. Tap it on a phone and your Carduzo card opens.</p>
          </div>
        </div>
        <div className="rounded-3xl bg-surface p-7 sm:p-9 flex flex-col sm:flex-row gap-7 items-start sm:items-center">
          <div className="w-[120px] h-[150px] rounded-2xl bg-navy p-3 shrink-0 flex flex-col justify-between shadow-[0_14px_30px_rgba(11,21,48,0.3)]" aria-hidden="true">
            <img src={BRAND.logo.mark} alt="" className="h-7 w-auto" />
            <div className="space-y-1.5"><div className="h-2 w-16 rounded bg-white/70" /><div className="h-2 w-10 rounded bg-white/35" /></div>
            <div className="h-10 w-10 rounded bg-white self-end" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2.5"><h3 className="font-display font-bold text-[24px]">Apple Wallet</h3><SoonTag /></div>
            <p className="mt-2 text-[16px] leading-[25px] text-ink-muted">Keep your card in Wallet so your QR code is a double-click away.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

const faq = () => [
  ['Do the people I meet need an app?', 'No. Your card opens in their phone’s web browser from the QR code. Save contact adds you straight to their contacts on iPhone or Android.'],
  ['Do visitors have to give me their details?', 'No. Lead capture invites them to share, but a small “Download the contact without sharing” link is always there. You can also switch lead capture off.'],
  ['What if I change jobs or my number?', 'Edit your card and the change is live right away. Your link and QR code stay the same, so anything you already printed keeps working.'],
  ['Can I use my own photo and company logo?', 'Yes. Upload a profile photo and it shows on all five layouts. On Teams, the company logo and colors are set once for everyone.'],
  ['Is there a free trial?', TRIAL_DAYS
    ? `Yes. Pro and Teams start with a ${TRIAL_DAYS}-day free trial. You add a card at checkout and aren’t charged until the trial ends; cancel any time before that from Billing. The Free plan stays free.`
    : 'The Free plan is free for as long as you like, so you can try Carduzo before you pay. Cancel Pro or Teams any time from Billing.'],
  ['What is a seat on Teams?', `One seat is one person with a card. Teams starts at ${TEAM_MIN_SEATS} seats, and you choose how many you need at checkout.`],
  ['When are NFC cards and Apple Wallet coming?', 'They’re next on our list. Your digital card and QR code work today, and NFC cards will open the same card.'],
];

function Faq() {
  usePricing();
  return (
    <section id="faq" aria-labelledby="faq-title" className="py-20 sm:py-28 bg-surface">
      <div className={`${container} grid lg:grid-cols-[1fr_1.6fr] gap-10`}>
        <SectionHead id="faq-title" title="Questions, answered" />
        <div className="border-t-2 border-navy">
          {faq().map(([q, a]) => (
            <details key={q} className="group border-b border-line">
              <summary className="flex items-center justify-between gap-4 py-5 cursor-pointer list-none [&::-webkit-details-marker]:hidden font-display font-bold text-[19px] leading-[26px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue rounded">
                {q}
                <span className="w-8 h-8 rounded-full border border-line-strong flex items-center justify-center shrink-0 transition group-open:rotate-45" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                </span>
              </summary>
              <p className="pb-6 -mt-1 text-[16px] leading-[26px] text-ink-muted max-w-[60ch]">{a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="bg-navy text-white relative overflow-hidden">
      <svg viewBox="0 0 400 400" className="absolute -left-24 -bottom-40 w-[420px] h-[420px] pointer-events-none" aria-hidden="true">
        <g fill="none" stroke="#8B5CF6" strokeWidth="10" strokeLinecap="round">
          <path d="M120 300a110 110 0 0 1 110-110" opacity="0.7" />
          <path d="M120 300a170 170 0 0 1 170-170" opacity="0.4" />
          <path d="M120 300a230 230 0 0 1 230-230" opacity="0.2" />
        </g>
      </svg>
      <div className={`${container} relative py-20 sm:py-28 flex flex-col items-start sm:items-center sm:text-center`}>
        <h2 className="font-display font-extrabold text-[40px] leading-[44px] sm:text-[60px] sm:leading-[62px] tracking-[-0.035em] max-w-[14ch]">
          Your next introduction starts here.
        </h2>
        <p className="mt-5 text-[18px] leading-[28px] text-[#C9D6E6] max-w-[36em]">Make your card in a few minutes. It’s free for one card, and you can upgrade when you want the leads.</p>
        <div className="mt-8 flex flex-wrap gap-3 sm:justify-center">
          <Link to={signupPath('free')} className={ctaPrimary}>Start free</Link>
          <Link to={signupPath('teams', 'year')} className={ctaGhostDark}>Set up a team</Link>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-navy text-[#9FB3CC] border-t border-white/10">
      <div className={`${container} py-8 flex flex-wrap items-center gap-x-8 gap-y-4 text-[14px]`}>
        <img src={BRAND.logo.lockupOnDark} alt={BRAND.name} className="h-7 w-auto" />
        <span>© {new Date().getFullYear()} {BRAND.name} · {BRAND.parentLine}</span>
        <nav className="flex gap-5 ml-auto" aria-label="Footer">
          <a href="#pricing" className="hover:text-white">Pricing</a>
          <Link to="/login" className="hover:text-white">Sign in</Link>
        </nav>
      </div>
    </footer>
  );
}
